import * as React from 'react';
import { useNavigate } from 'react-router';
import {
  useListHunts,
  useCreateHunt,
  useDeleteHunt,
  getListHuntsQueryKey,
  type CreateHuntRequest,
} from '@cdf/api-client-react';
import { useQueryClient } from '@tanstack/react-query';
import { PlusIcon, PencilIcon, Trash2Icon, TrophyIcon, ExternalLinkIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { PageHeader } from '@/components/layout/page-header';

function StatusBadge({ status }: { status: string }) {
  if (status === 'active')
    return <Badge className="bg-green-600 text-white hover:bg-green-600">Active</Badge>;
  if (status === 'closed') return <Badge variant="outline">Closed</Badge>;
  return <Badge variant="secondary">Draft</Badge>;
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function HuntCreateDialog({
  open,
  onOpenChange,
  onSubmit,
  saving,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: CreateHuntRequest) => void;
  saving: boolean;
}) {
  const [name, setName] = React.useState('');
  const [slug, setSlug] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [bonus, setBonus] = React.useState('50');
  const [slugTouched, setSlugTouched] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setName('');
      setSlug('');
      setDescription('');
      setBonus('50');
      setSlugTouched(false);
    }
  }, [open]);

  const effectiveSlug = slugTouched ? slug : slugify(name);
  const valid = name.trim().length > 0 && /^[a-z0-9-]+$/.test(effectiveSlug);

  const handleSubmit = () => {
    if (!valid) return;
    onSubmit({
      slug: effectiveSlug,
      name: name.trim(),
      description: description.trim() || undefined,
      completionBonusPoints: parseInt(bonus, 10) || 0,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Create Treasure Hunt</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="hunt-name">Name</Label>
            <Input
              id="hunt-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Conference Treasure Hunt"
              maxLength={100}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="hunt-slug">Slug</Label>
            <Input
              id="hunt-slug"
              value={effectiveSlug}
              onChange={(e) => {
                setSlug(e.target.value);
                setSlugTouched(true);
              }}
              placeholder="conference-treasure-hunt"
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground">
              Used in the public URL: /hunt/{effectiveSlug || '…'}
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="hunt-desc">Description</Label>
            <Textarea
              id="hunt-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="A Pokemon Go-style game for the conference…"
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="hunt-bonus">Completion Bonus Points</Label>
            <Input
              id="hunt-bonus"
              type="number"
              min={0}
              value={bonus}
              onChange={(e) => setBonus(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Bonus points awarded for capturing all stations
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!valid || saving}>
            {saving ? 'Creating…' : 'Create Hunt'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function HuntAdminPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isLoading } = useListHunts();
  const createMutation = useCreateHunt();
  const deleteMutation = useDeleteHunt();

  const [createOpen, setCreateOpen] = React.useState(false);
  const [deleteTarget, setDeleteTarget] = React.useState<{ id: string; name: string } | null>(null);

  const hunts = data?.hunts ?? [];

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: getListHuntsQueryKey() });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Hunt Admin"
        icon={TrophyIcon}
        description="Create and manage conference treasure hunts"
        actions={
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <PlusIcon className="mr-2 h-4 w-4" /> Create Hunt
          </Button>
        }
      />

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Bonus Pts</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-muted-foreground">
                    Loading…
                  </TableCell>
                </TableRow>
              ) : hunts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-muted-foreground">
                    No hunts yet. Create one to get started.
                  </TableCell>
                </TableRow>
              ) : (
                hunts.map((hunt) => (
                  <TableRow key={hunt.id}>
                    <TableCell className="font-medium">{hunt.name}</TableCell>
                    <TableCell className="font-mono text-sm text-muted-foreground">
                      {hunt.slug}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={hunt.status} />
                    </TableCell>
                    <TableCell>{hunt.completionBonusPoints}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(hunt.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {hunt.status !== 'draft' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            asChild
                            title="View leaderboard"
                          >
                            <a
                              href={`/hunt/${hunt.slug}/leaderboard/live`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <ExternalLinkIcon className="h-4 w-4" />
                            </a>
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(`/workspace/hunts/${hunt.id}`)}
                          title="Edit hunt"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            setDeleteTarget({ id: hunt.id, name: hunt.name })
                          }
                          title="Delete hunt"
                        >
                          <Trash2Icon className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <HuntCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        saving={createMutation.isPending}
        onSubmit={async (values) => {
          const hunt = await createMutation.mutateAsync({ data: values });
          setCreateOpen(false);
          invalidate();
          navigate(`/workspace/hunts/${hunt.id}`);
        }}
      />

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete hunt?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete &ldquo;{deleteTarget?.name}&rdquo;? This will remove
            all stations, challenges, and participant data. This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={async () => {
                if (!deleteTarget) return;
                await deleteMutation.mutateAsync({ huntId: deleteTarget.id });
                invalidate();
                setDeleteTarget(null);
              }}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
